import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  WritableSignal,
  signal,
  OnChanges,
  SimpleChanges,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DxButtonModule, DxDataGridModule, DxFormModule } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { firstValueFrom } from 'rxjs';
import { AddMemoDto, AssignmentService, AssignmentType, MemoDto, UpdateMemoDto } from '../../shared/generated/backend';
import { SharedModule } from '../../shared/shared.module';
import { createBusyState } from '../../shared/util/busy-state';

@Component({
  selector: 'app-assignment-memos',
  imports: [SharedModule, FormsModule, DxFormModule, DxButtonModule, DxDataGridModule],
  templateUrl: './assignment-memos.component.html',
  styleUrls: ['./assignment-memos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentMemosComponent implements OnChanges {
  readonly assignmentId = input.required<number>();
  readonly type = input.required<AssignmentType>();

  readonly isLoading: WritableSignal<boolean> = signal(false);
  private readonly busy = createBusyState();
  readonly isBusy = this.busy.busy;
  selectedMemo: WritableSignal<MemoDto | null> = signal(null);
  memos: WritableSignal<MemoDto[]> = signal([]);
  readonly internalMemos = computed(() => this.memos().filter(memo => memo.InternMemo === 1));
  readonly assignmentMemos = computed(() => this.memos().filter(memo => (memo.InternMemo ?? 0) !== 1));
  isDirty: boolean = false;

  expandedMemo: MemoDto | null = null;
  newMemo: boolean = false;
  formType: 'edit' | 'create' = 'edit';

  formData: MemoDto = {
    Id: 0,
    Subject: '',
    SubjectUser: '',
    MemoText: '',
    CreatedAt: '',
    CreatedBy: '',
    InternMemo: 1,
  };

  private assignmentService = inject(AssignmentService);
  private translateService = inject(TranslateService);

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['assignmentId'] && !changes['assignmentId'].firstChange) {
      this.cancelNewMemo();
    }
    this.isLoading.set(true);
    try {
      const dto = await firstValueFrom(this.assignmentService.getMemos(this.assignmentId(), this.type()));
      this.memos.set(dto);
    } finally {
      this.isLoading.set(false);
    }
  }

  expandMemo(memo: MemoDto) {
    this.newMemo = false;
    this.formType = 'edit';
    this.expandedMemo = this.expandedMemo === memo ? null : memo;
    if (this.expandedMemo) {
      this.formData = {
        ...this.expandedMemo,
        MemoText: this.extractPlainTextFromRTF(this.expandedMemo.MemoText || ''),
      };
    }
  }

  createNewMemo() {
    this.newMemo = true;
    this.formType = 'create';
    this.expandedMemo = null;
    this.formData = {
      Id: 0,
      Subject: '',
      SubjectUser: '',
      MemoText: '',
    };
  }

  cancelNewMemo() {
    this.newMemo = false;
    this.formType = 'edit';
    this.formData = {
      Id: 0,
      Subject: '',
      SubjectUser: '',
      MemoText: '',
    };
  }

  async save() {
    if (!this.isDirty) return;
    await this.busy.run(async () => {
      try {
        this.isLoading.set(true);
        if (this.formType === 'edit' && !this.newMemo && this.expandedMemo) {
          if (this.expandedMemo.InternMemo !== 1) {
            return;
          }
          const data: UpdateMemoDto = {
            Subject: this.formData.Subject,
            MemoText: this.escapeSingleQuotes(this.formData.MemoText || ''),
          };
          if (this.expandedMemo?.Id !== undefined) {
            await firstValueFrom(this.assignmentService.updateMemo(this.expandedMemo.Id, data));
          }
          await this.refreshTableAndOpenView(this.formData.Id);
        } else if (this.formType === 'create') {
          // Create new memo
          const data: AddMemoDto = {
            Subject: this.formData.Subject,
            MemoText: this.formData.MemoText,
            AssignmentNo: 0,
          };
          await firstValueFrom(this.assignmentService.addMemo(this.assignmentId(), this.type(), data));
          this.cancelNewMemo();
          await this.refreshTableAndOpenView();
        }
      } catch {
        notify({ message: this.translateService.instant('ERROR_STATUS.Unkown_Error') }, 'error', 5_000);
      } finally {
        this.isLoading.set(false);
      }
    });
  }

  async delete() {
    if (this.expandedMemo === null) return;
    if (this.expandedMemo.InternMemo !== 1) return;
    await this.busy.run(async () => {
      try {
        const confirmed = await confirm(
          this.translateService.instant('MEMO.DTO.ASK_DELETE'),
          this.translateService.instant('MEMO.DTO.DELETE_MEMO')
        );
        if (!confirmed) return;

        this.isLoading.set(true);
        await firstValueFrom(this.assignmentService.deleteMemo(this.expandedMemo?.Id ?? 0));
        await this.refreshTableAndOpenView();
      } catch {
        notify({ message: this.translateService.instant('ERROR_STATUS.Unkown_Error') }, 'error', 5_000);
      } finally {
        this.isLoading.set(false);
      }
    });
  }

  async refreshTableAndOpenView(memoId?: number) {
    this.isLoading.set(true);
    try {
      const dto = await firstValueFrom(this.assignmentService.getMemos(this.assignmentId(), this.type()));
      this.memos.set(dto);

      if (memoId !== undefined) {
        const memo = dto.find(m => m.Id === memoId);
        if (memo) {
          this.selectedMemo.set(memo);
          this.expandMemo(memo);
        }
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  escapeSingleQuotes(text: string): string {
    return text.replace(/'/g, "''");
  }

  extractPlainTextFromRTF(rtf: string): string {
    const rtfRegex = /\\[a-z]+(-?\d+)?(\s)?|{\\.*?}|}|{|\\\\/g;
    let plainText = rtf.replace(rtfRegex, '');
    plainText = plainText.replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    plainText = plainText.replace(/\\\\/g, '\\');
    plainText = plainText.replace(/\\par/g, '\n');
    plainText = plainText.replace(/\\tab/g, '\t');
    return plainText.trim();
  }
}
